
import { dbManager } from './db_manager';
import { trackAchievement } from './achievementService';
import { streakService } from './streakService';

export interface Profile {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string;
    bio?: string;
    avatar_config?: any;
    plan_type?: string;
    subscription_tier?: string;
    current_streak?: number;
    streak?: number;
    last_activity_date?: string;
    longest_streak?: number;
    email?: string;
    city?: string;
    state?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    native_language?: string;
    tutorial_completed?: 'yes' | 'no';
}

export const profileService = {
    async getProfile(userId: string): Promise<Profile | null> {
        const results = await dbManager.from('profiles', userId).select({ id: userId });
        if (results && results.length > 0) {
            const raw = results[0];
            // The D1 worker may nest profile fields under a 'profiles' key when a JOIN is performed.
            // For direct authenticated selects, data should be at root. Handle both shapes defensively.
            const profile: any = raw.profiles ? { ...raw, ...raw.profiles } : { ...raw };
            if (profile.avatar_config && typeof profile.avatar_config === 'string') {
                try { profile.avatar_config = JSON.parse(profile.avatar_config); } catch (e) { }
            }
            if (profile.old_usernames && typeof profile.old_usernames === 'string') {
                try { profile.old_usernames = JSON.parse(profile.old_usernames); } catch (e) { profile.old_usernames = []; }
            }
            return profile as Profile;
        }
        return null;
    },

    async updateProfile(userId: string, data: Partial<Profile>) {
        return dbManager.from('profiles', userId).upsert({
            id: userId,
            ...data,
            updated_at: new Date().toISOString()
        });
    },

    async getProfileByUsername(username: string): Promise<Profile | null> {
        const response = await dbManager.execute({
            action: 'select_public',
            table: 'profiles',
            filters: { username }
        });
        const arr = Array.isArray(response) ? response : (response?.data || []);
        if (arr.length > 0) {
            const raw = arr[0];
            const profile: any = raw.profiles ? { ...raw, ...raw.profiles } : { ...raw };
            if (profile.avatar_config && typeof profile.avatar_config === 'string') {
                try { profile.avatar_config = JSON.parse(profile.avatar_config); } catch (e) { }
            }
            return profile as Profile;
        }
        return null;
    },

    /**
     * Proactive refresh for a target user profile.
     * In this implementation, select_public always hits D1, but this ensures 
     * any future frontend caching is bypassed.
     */
    async refreshTargetProfile(username: string) {
        console.log(`[ProfileService] Proactive refresh for: ${username}`);
        return this.getProfileByUsername(username);
    },
    
    async checkUsernameAvailability(username: string, excludeUserId?: string): Promise<boolean> {
        const results = await dbManager.execute({
            action: 'select_public',
            table: 'profiles',
            filters: { username: username.trim().toLowerCase() }
        });
        
        if (results && results.length > 0) {
            // Check if the owner is not the current user
            if (excludeUserId) {
                return !results.some(p => p.id === excludeUserId);
            }
            return false; // Taken
        }
        return true; // Available
    },
    
    async incrementStreak(userId: string) {
        // Legacy redirect to centralized streak service
        return streakService.validateAndIncrementStreak(userId, 'other');
    }
};
